/**
 * Slovenian Translations — Impact Core i18n
 *
 * Flat key-value structure. Keys use dot notation for namespacing.
 * Coverage: navigation, actions, statuses, dashboard labels, form labels.
 */

export const sl: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Nadzorna plošča',
  'nav.funds': 'Skladi',
  'nav.investments': 'Naložbe',
  'nav.investees': 'Prejemniki naložb',
  'nav.esg': 'ESG Pregled',
  'nav.compliance': 'Skladnost',
  'nav.reports': 'Poročila',
  'nav.settings': 'Nastavitve',
  'nav.users': 'Uporabniki',
  'nav.sfdr': 'SFDR',
  'nav.pai': 'PAI Kazalniki',
  'nav.taxonomy': 'Taksonomija EU',
  'nav.filings': 'Vložitve',
  'nav.calendar': 'Koledar',
  'nav.submit': 'Oddaj podatke',
  'nav.submissions': 'Moje oddaje',
  'nav.benchmarks': 'Referenčne vrednosti',
  'nav.messages': 'Sporočila',
  'nav.portfolio': 'Portfelj',
  'nav.performance': 'Uspešnost skladov',
  'nav.esgSummary': 'ESG Povzetek',
  'nav.pipeline': 'Cevovod',
  'nav.dueDiligence': 'Skrbni pregled',
  'nav.impact': 'Kazalniki učinka',
  'nav.sdg': 'Kartiranje CRT',
  'nav.audit': 'Revizijska sled',
  'nav.snapshots': 'Posnetki podatkov',
  'nav.knowledgeGraph': 'Graf znanja',
  'nav.geographic': 'Geografski',
  'nav.bi': 'BI Studio',
  'nav.ai': 'AI Pomočnik',
  'nav.tasks': 'Naloge',
  'nav.csrd': 'CSRD',
  'nav.regulatory': 'Regulativni monitor',

  // Common Actions
  'action.create': 'Ustvari',
  'action.edit': 'Uredi',
  'action.delete': 'Izbriši',
  'action.save': 'Shrani',
  'action.cancel': 'Prekliči',
  'action.submit': 'Oddaj',
  'action.approve': 'Odobri',
  'action.reject': 'Zavrni',
  'action.download': 'Prenesi',
  'action.export': 'Izvozi',
  'action.import': 'Uvozi',
  'action.filter': 'Filtriraj',
  'action.search': 'Išči',
  'action.refresh': 'Osveži',
  'action.close': 'Zapri',
  'action.back': 'Nazaj',
  'action.next': 'Naprej',
  'action.previous': 'Prejšnje',
  'action.viewAll': 'Prikaži vse',
  'action.viewDetails': 'Prikaži podrobnosti',
  'action.generate': 'Generiraj',
  'action.signOut': 'Odjava',
  'action.addNew': 'Dodaj novo',
  'action.invite': 'Povabi',

  // Statuses
  'status.draft': 'Osnutek',
  'status.submitted': 'Oddano',
  'status.validated': 'Potrjeno',
  'status.approved': 'Odobreno',
  'status.rejected': 'Zavrnjeno',
  'status.pending': 'V čakanju',
  'status.inProgress': 'V teku',
  'status.overdue': 'Zamujeno',
  'status.compliant': 'Skladno',
  'status.filed': 'Vloženo',
  'status.active': 'Aktivno',
  'status.inactive': 'Neaktivno',
  'status.closed': 'Zaprto',
  'status.fundraising': 'Zbiranje sredstev',
  'status.investing': 'Nalaganje',
  'status.harvesting': 'Žetev',
  'status.validating': 'Preverjanje',
  'status.review': 'V pregledu',
  'status.monitoring': 'Spremljanje',
  'status.exiting': 'Izstop',
  'status.pipeline': 'Pipeline',
  'status.due_diligence': 'Skrbni pregled',
  'status.disbursed': 'Izplačano',
  'status.restructured': 'Prestrukturirano',
  'status.matured': 'Zapadlo',
  'status.written_off': 'Odpisano',
  'status.exited': 'Izstopil',
  'status.incomplete': 'Nepopolno',
  'status.open': 'Odprto',
  'status.in_progress': 'V teku',
  'status.completed': 'Zaključeno',
  'status.cancelled': 'Preklicano',
  'status.accepted': 'Sprejeto',
  'status.failed': 'Neuspešno',

  // Dashboard Labels
  'dashboard.portfolioOverview': 'Pregled portfelja',
  'dashboard.complianceStatus': 'Stanje skladnosti',
  'dashboard.collectionProgress': 'Napredek zbiranja',
  'dashboard.activityFeed': 'Dejavnost',
  'dashboard.quickActions': 'Hitre akcije',
  'dashboard.investmentPipeline': 'Naložbeni cevovod',
  'dashboard.totalFunds': 'Skupaj skladov',
  'dashboard.aum': 'AUM',
  'dashboard.activeInvestments': 'Aktivne naložbe',
  'dashboard.totalInvestees': 'Prejemniki naložb',
  'dashboard.recentActivity': 'Nedavna dejavnost',

  // LP Dashboard Labels
  'lp.totalCommitted': 'Skupaj zavezano',
  'lp.capitalCalled': 'Poklicani kapital',
  'lp.distributed': 'Razdeljeno',
  'lp.netPosition': 'Neto pozicija',
  'lp.tvpi': 'TVPI',
  'lp.dpi': 'DPI',
  'lp.fundPerformance': 'Uspešnost skladov',
  'lp.esgSummary': 'ESG Povzetek',
  'lp.reportLibrary': 'Knjižnica poročil',
  'lp.taxonomyAligned': 'Usklajeno s taksonomijo',
  'lp.paiCoverage': 'PAI Pokritost',
  'lp.portfolioPerformance': 'Uspešnost LP portfelja',

  // Fund Labels
  'fund.name': 'Ime sklada',
  'fund.code': 'Koda sklada',
  'fund.vintage': 'Leto ustanovitve',
  'fund.size': 'Velikost sklada',
  'fund.targetSize': 'Ciljna velikost',
  'fund.currency': 'Valuta',
  'fund.strategy': 'Strategija',
  'fund.status': 'Stanje',
  'fund.sfdrCategory': 'SFDR Kategorija',
  'fund.geographyFocus': 'Geografski fokus',
  'fund.sectorFocus': 'Sektorski fokus',

  // Form Labels
  'form.name': 'Ime',
  'form.email': 'E-pošta',
  'form.country': 'Država',
  'form.amount': 'Znesek',
  'form.currency': 'Valuta',
  'form.date': 'Datum',
  'form.description': 'Opis',
  'form.notes': 'Opombe',
  'form.type': 'Vrsta',
  'form.period': 'Obdobje',
  'form.year': 'Leto',
  'form.required': 'Obvezno',
  'form.optional': 'Neobvezno',

  // Table Headers
  'table.fund': 'Sklad',
  'table.commitment': 'Zaveza',
  'table.called': 'Poklicano',
  'table.distributed': 'Razdeljeno',
  'table.tvpi': 'TVPI',
  'table.status': 'Stanje',
  'table.regulation': 'Predpis',
  'table.filingType': 'Vrsta vložitve',
  'table.reportingPeriod': 'Obdobje',
  'table.createdAt': 'Ustvarjeno',
  'table.actions': 'Dejanja',

  // Compliance
  'compliance.sfdr': 'SFDR',
  'compliance.taxonomy': 'Taksonomija EU',
  'compliance.pai': 'PAI',
  'compliance.dataCompleteness': 'Popolnost podatkov',
  'compliance.nextDeadline': 'Naslednji rok',

  // Reports
  'report.quarterly': 'Četrtletno poročilo',
  'report.annual': 'Letno poročilo',
  'report.paiStatement': 'PAI Izjava',
  'report.taxonomyReport': 'Poročilo o taksonomiji',
  'report.generatedAt': 'Generirano',
  'report.version': 'Različica',

  // General
  'general.loading': 'Nalaganje...',
  'general.noData': 'Ni razpoložljivih podatkov',
  'general.error': 'Prišlo je do napake',
  'general.retry': 'Poskusi znova',
  'general.total': 'Skupaj',
  'general.page': 'Stran',
  'general.of': 'od',
  'general.showing': 'Prikazano',
  'general.results': 'rezultatov',
  'general.language': 'Jezik',
  'general.english': 'Angleščina',
  'general.french': 'Francoščina',

  // Tour
  'tour.takeTour': 'Zacni ogled',
  'tour.skip': 'Preskoci',
  'tour.next': 'Naprej',
  'tour.prev': 'Nazaj',
  'tour.finish': 'Koncaj',


  // Tour Content, Form Labels, Table Headers, Actions, Errors (i18n wiring)
  'tour.sidebarTitle': 'Navigacija',
  'tour.sidebarContent': 'Uporabite stransko vrstico za krmarjenje med upravljanjem portfelja, zbiranjem podatkov ESG, skladnostjo, poročili in analitičnimi orodji.',
  'tour.kpisTitle': 'Pregled portfelja',
  'tour.kpisContent': 'Te kartice prikazujejo vaš portfelj na prvi pogled — skupni skladi, AUM, aktivne naložbe in število naložbencev.',
  'tour.actionsTitle': 'Hitri ukrepi',
  'tour.actionsContent': 'Ustvarjajte sklade, dodajajte naložbe, povabite naložbence in ustvarjajte poročila z enim klikom.',
  'tour.languageTitle': 'Jezik',
  'tour.languageContent': 'Preklapljajte med 24 uradnimi jeziki EU. Celoten vmesnik se prevede takoj.',
  'tour.aiTitle': 'AI Pomočnik',
  'tour.aiContent': 'Postavite vprašanja o svojem portfelju kadarkoli. AI pomočnik ima poln kontekst o vaših skladih in naložbah.',
  'action.createFund': 'Ustvari sklad',
  'action.editFund': 'Uredi sklad',
  'action.addInvestment': 'Dodaj naložbo',
  'action.addInvestee': 'Dodaj naložbenca',
  'action.editInvestee': 'Uredi naložbenca',
  'action.createTemplate': 'Ustvari predlogo',
  'action.createSubmission': 'Ustvari oddajo',
  'action.createFiling': 'Ustvari vlogo',
  'action.generateReport': 'Ustvari poročilo',
  'action.inviteUser': 'Povabi uporabnika',
  'action.sendInvitation': 'Pošlji povabilo',
  'action.saveChanges': 'Shrani spremembe',
  'form.fundName': 'Ime sklada',
  'form.fundCode': 'Koda sklada',
  'form.sfdrCategory': 'SFDR Kategorija',
  'form.baseCurrency': 'Osnovna valuta',
  'form.targetSize': 'Ciljna velikost',
  'form.vintageYear': 'Leto ustanovitve',
  'form.geographyFocus': 'Geografski fokus',
  'form.sectorFocus': 'Sektorski fokus',
  'form.investmentCode': 'Koda naložbe',
  'form.instrumentType': 'Vrsta instrumenta',
  'form.maturityDate': 'Datum zapadlosti',
  'form.sector': 'Sektor',
  'form.countryCode': 'Koda države',
  'form.entityType': 'Vrsta subjekta',
  'form.region': 'Regija',
  'form.staffCount': 'Število zaposlenih',
  'form.borrowers': 'Posojilojemalci',
  'form.contactName': 'Ime kontakta',
  'form.contactEmail': 'Kontaktni e-mail',
  'form.templateName': 'Ime predloge',
  'form.templateType': 'Vrsta predloge',
  'form.indicators': 'Kazalniki',
  'form.reportingPeriod': 'Poročevalsko obdobje',
  'form.reportingYear': 'Poročevalsko leto',
  'form.regulation': 'Regulacija',
  'form.filingType': 'Vrsta vloge',
  'form.reportType': 'Vrsta poročila',
  'form.emailAddress': 'E-poštni naslov',
  'form.role': 'Vloga',
  'form.selectFund': 'Izberite sklad...',
  'form.selectInvestee': 'Izberite naložbenca...',
  'table.code': 'Koda',
  'table.investee': 'Naložbenec',
  'table.instrument': 'Instrument',
  'table.amount': 'Znesek',
  'table.currency': 'Valuta',
  'table.maturity': 'Zapadlost',
  'table.name': 'Ime',
  'table.country': 'Država',
  'table.type': 'Vrsta',
  'table.period': 'Obdobje',
  'table.completion': 'Zaključek',
  'table.submitted': 'Oddano',
  'table.version': 'Različica',
  'table.indicators': 'Kazalniki',
  'table.dueDate': 'Rok',
  'table.generated': 'Ustvarjeno',
  'table.email': 'E-pošta',
  'table.role': 'Vloga',
  'error.createFailed': 'Ustvarjanje ni uspelo. Poskusite znova.',
  'error.updateFailed': 'Posodobitev ni uspela. Poskusite znova.',
  'error.loadFailed': 'Nalaganje podatkov ni uspelo.',

  // Sidebar Sections
  'sections.portfolio': 'PORTFELJ',
  'sections.dataCollection': 'ZBIRANJE PODATKOV',
  'sections.intelligence': 'OBVEŠČEVALNA',
  'sections.ecosystem': 'EKOSISTEM',
  'sections.admin': 'ADMINISTRACIJA',
  'sections.other': 'DRUGO',

  // Page Titles
  'pages.messagesTitle': 'Sporočila',
  'pages.taxonomyTitle': 'Taksonomija EU',
  'pages.biStudioTitle': 'BI Studio',
  'pages.knowledgeGraphTitle': 'Graf znanja',
  'pages.benchmarksTitle': 'Merila',
  'pages.mcpTitle': 'Integracija MCP',
  'pages.tasksTitle': 'Naloge',
  'pages.submitDataTitle': 'Pošlji ESG podatke',

  // Empty States
  'empty.benchmarksComingSoon': 'Merila kmalu na voljo',

  // Labels
  'labels.sfdr': 'SFDR',
  'labels.euTaxonomy': 'Taksonomija EU',
  'labels.csrd': 'CSRD',
  'labels.mifid': 'MiFID',
  'labels.other': 'Drugo',
  'labels.critical': 'Kritično',
  'labels.high': 'Visoko',
  'labels.medium': 'Srednje',
  'labels.low': 'Nizko',

  // Count / summary strings
  'count.fundsTotal': '{count} fund(s) total',
  'count.investmentsTotal': '{count} investment(s) total',
  'count.investeesTotal': '{count} investee(s) total',

  // Filter options
  'filter.allStatus': 'All Status',
  'filter.allSfdr': 'All SFDR',

  // Empty states
  'empty.noFundsYet': 'No funds yet. Create your first fund.',
  'empty.noFundsMatch': 'No funds match your filters.',
  'empty.noInvestmentsYet': 'No investments yet.',
  'empty.noInvestmentsMatch': 'No investments match your search.',
  'empty.noInvesteesYet': 'No investees yet.',
  'empty.noInvesteesMatch': 'No investees match your search.',
  'empty.noTemplatesYet': 'No ESG templates configured yet.',
  'empty.noSubmissionsYet': 'No ESG submissions yet.',
  'empty.noComplianceData': 'No SFDR compliance data available.',
  'empty.noFilingsYet': 'No compliance filings yet.',
  'empty.noReportsYet': 'No reports generated yet.',
  'empty.selectFundPai': 'Select a fund to view PAI indicators.',
  'empty.noPaiIndicators': 'No PAI indicators found for this fund.',
  'empty.selectFundCalendar': 'Select a fund to view compliance calendar.',
  'empty.noCalendarEvents': 'No upcoming events for this fund.',

  // Page subtitles
  'subtitle.esg': 'Templates and data submissions',
  'subtitle.compliance': 'SFDR, PAI indicators, and regulatory filings',
  'subtitle.reports': 'Generate and manage compliance reports',
  'subtitle.settings': 'Manage your account and preferences',

  // Tab labels
  'tab.templates': 'Templates',
  'tab.submissions': 'Submissions',
  'tab.sfdr': 'SFDR',
  'tab.paiIndicators': 'PAI Indicators',
  'tab.filings': 'Filings',
  'tab.reports': 'Reports',
  'tab.calendar': 'Calendar',

  // Compliance table headers
  'table.indicator': 'Indicator',
  'table.category': 'Category',
  'table.metric': 'Metric',
  'table.value': 'Value',
  'table.mandatory': 'Mandatory',
  'table.data': 'Data',
  'table.deadline': 'Deadline',
  'table.taxonomy': 'Taxonomy',

  // Yes/No
  'general.yes': 'Yes',
  'general.no': 'No',

  // Error messages
  'error.loadTemplates': 'Failed to load templates.',
  'error.loadSubmissions': 'Failed to load submissions.',
  'error.loadCompliance': 'Failed to load compliance data.',
  'error.loadPai': 'Failed to load PAI indicators.',
  'error.loadFilings': 'Failed to load filings.',
  'error.loadCalendar': 'Failed to load calendar events.',
  'error.createTemplate': 'Failed to create template. Please try again.',
  'error.createSubmission': 'Failed to create submission. Please try again.',
  'error.createFiling': 'Failed to create filing. Please try again.',
  'error.generateReport': 'Failed to generate report.',
  'error.templateNameRequired': 'Template name is required',
  'error.selectFund': 'Select a fund',
  'error.selectInvestee': 'Select an investee',
  'error.validAmount': 'Enter a valid amount',
  'error.reportingPeriodRequired': 'Reporting period is required',
  'error.validYearRequired': 'Valid year is required',
  'error.enterPeriod': 'Enter a period',
  'error.nameRequired': 'Name is required',
  'error.countryCodeRequired': 'Country code is required',
  'error.operationFailed': 'Operation failed.',
  'error.pleaseselectFund': 'Please select a fund',

  // Success messages
  'success.submissionCreated': 'Submission created successfully.',
  'success.profileSaved': 'Profile saved successfully.',

  // Relative time
  'time.justNow': 'Just now',
  'time.minutesAgo': '{count}m ago',
  'time.hoursAgo': '{count}h ago',
  'time.daysAgo': '{count}d ago',

  // Settings
  'settings.profile': 'Profile',
  'settings.organization': 'Organization',
  'settings.apiKeys': 'API Keys',
  'settings.notifications': 'Notifications',
  'settings.orgId': 'Org ID',
  'settings.tier': 'Tier',
  'settings.apiKeysDescription': 'Contact your organization administrator to manage API keys and integration credentials.',
  'settings.apiKeysNote': 'API keys provide programmatic access to Impact Core data. For security, key creation and revocation is restricted to organization admins.',
  'settings.notifReportAlerts': 'Report generation alerts',
  'settings.notifReportAlertsDesc': 'Email when a report is generated or fails',
  'settings.notifComplianceReminders': 'Compliance deadline reminders',
  'settings.notifComplianceRemindersDesc': 'Email reminders for upcoming filing deadlines',
  'settings.notifEsgUpdates': 'ESG submission updates',
  'settings.notifEsgUpdatesDesc': 'Email when investee submissions change status',
  'settings.notifInAppActivity': 'In-app activity feed',
  'settings.notifInAppActivityDesc': 'Show real-time activity notifications in header',

  // Data Completeness label
  'compliance.dataCompletenessLabel': 'Data Completeness',
};
